/**
 * Per-Meta-ad detail route (#5981) — the Meta sibling of
 * ``routes/.../google/$campaignslug/_slug/ad/$adId.tsx``.
 *
 * Same shell as the Meta campaign route (sidebar left, detail panel right); the panel scopes to
 * one ad. The ``$metaCampaignSlug_`` segment opts out of nesting under the campaign route file
 * (which renders no <Outlet>), while keeping the ``/campaigns/meta/{slug}/ad/{id}`` path.
 */

import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { createFileRoute, useParams } from '@tanstack/react-router';

import { campaignDataAtom, metaCampaignDataAtom } from '@/store';
import { CampaignSidebar, type PlatformFilter } from '@/components/campaign/campaign-sidebar';
import { CampaignSidebarToolbar } from '@/components/campaign/campaign-sidebar-toolbar';
import { MetaAdDetailPanel } from '@/components/campaign/meta/meta-ad-detail-panel';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { useCampaignSidebarFilters } from '@/hooks/useCampaignSidebarFilters';

export const Route = createFileRoute('/_authenticated/_layout/campaigns/meta/$metaCampaignSlug_/ad/$adId')({
  component: MetaAdPage,
});

function MetaAdPage() {
  const { metaCampaignSlug, adId } = useParams({
    from: '/_authenticated/_layout/campaigns/meta/$metaCampaignSlug_/ad/$adId',
  });
  const setMetaCampaignData = useSetAtom(metaCampaignDataAtom);
  const setCampaignData = useSetAtom(campaignDataAtom);

  // Keep the campaign selected in the sidebar (and expanded to its ads) while an ad is open —
  // mirrors the Google ad route keeping campaignDataAtom pointed at the parent campaign (#4660).
  useEffect(() => {
    setMetaCampaignData(metaCampaignSlug);
    setCampaignData(null);
  }, [metaCampaignSlug, setMetaCampaignData, setCampaignData]);

  // The persisted sidebar filters + FilterDropdown the Google ad route carries (#4446) — this
  // route previously hardcoded the filter sets and offered no filter control (#6163 P2, #6182).
  const { filterTypes, filterStatuses, filterPlatforms, updateFilters } = useCampaignSidebarFilters();
  const platformFilter: PlatformFilter =
    filterPlatforms.size === 1 && filterPlatforms.has('google') ? 'google' :
    filterPlatforms.size === 1 && filterPlatforms.has('meta') ? 'meta' :
    'all';

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>
      <div className="flex-1">
        <div className="grid grid-cols-12 gap-1">
          <div className="col-span-3 sticky top-0 self-start h-[calc(100vh-120px)]">
            <TabCardPanel
              tabs={[{ id: 'all', label: 'Campaigns', isActive: true }]}
              actions={
                <CampaignSidebarToolbar
                  filterTypes={filterTypes}
                  filterStatuses={filterStatuses}
                  filterPlatforms={filterPlatforms}
                  showPlatforms
                  onFiltersChange={updateFilters}
                />
              }
              cardClassName="overflow-hidden"
              noPadding
            >
              <CampaignSidebar
                filterTypes={filterTypes}
                filterStatuses={filterStatuses}
                platformFilter={platformFilter}
              />
            </TabCardPanel>
          </div>
          <div className="col-span-9">
            <MetaAdDetailPanel slug={metaCampaignSlug} adId={Number(adId)} />
          </div>
        </div>
      </div>
    </div>
  );
}
